<template>
  <div class="alice-obs">
    <!-- ============== Sub-page header ============== -->
    <header class="alice-obs__head">
      <div class="alice-obs__head-main">
        <div class="alice-obs__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Obslog</span>
        </div>
        <h1 class="alice-obs__title">
          Obslog
          <span v-if="hasObslog" class="alice-obs__count">{{
            notes.length
          }}</span>
        </h1>
        <p class="alice-obs__subtitle">
          Observaties en quotes uit je sessies. Importeer een spreadsheet of
          voeg notities live toe tijdens een interview.
        </p>
      </div>

      <div class="alice-obs__head-actions">
        <button
          v-if="!isGuest"
          class="alice-obs__head-btn"
          :class="{ 'alice-obs__head-btn--primary': !hasObslog }"
          :disabled="!project || importing || replacing"
          @click="onUploadButtonClick"
        >
          <Icon name="plus" :size="16" />
          <span>{{ uploadButtonLabel }}</span>
        </button>
        <button
          v-if="hasObslog"
          class="alice-obs__head-btn alice-obs__head-btn--primary"
          :class="{ 'alice-obs__head-btn--active': composerOpen }"
          @click="toggleComposer"
        >
          <Icon name="plus" :size="16" />
          <span>{{ composerOpen ? "Cancel" : "Add note" }}</span>
        </button>
        <input
          ref="uploadRef"
          type="file"
          accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style="display: none"
          @change="onFileChange"
          @click="resetUploadValue"
        />
      </div>
    </header>

    <!-- ============== Empty state ============== -->
    <div v-if="!project" class="alice-obs__empty">Loading project…</div>

    <div
      v-else-if="!hasObslog"
      class="alice-obs__empty alice-obs__empty--upload"
    >
      <div class="alice-obs__empty-pattern" aria-hidden="true">
        <VP1Pattern :size="64" :opacity="0.06" />
      </div>
      <div class="alice-obs__empty-inner">
        <p class="alice-obs__empty-headline">Nog geen obslog.</p>
        <p class="alice-obs__empty-body">
          Upload een spreadsheet (CSV / Excel) om de bestaande notities te
          importeren — of begin direct met losse notities.
        </p>
        <div
          v-if="!isGuest"
          class="alice-obs__dropzone"
          :class="{ 'is-dragover': dragOver }"
          @click="triggerObslogUpload"
          @dragenter.prevent="dragOver = true"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
        >
          <Icon name="plus" :size="20" />
          <div class="alice-obs__dropzone-text">
            Drop spreadsheet here, or
            <span class="alice-obs__dropzone-link">click to browse</span>
          </div>
          <div class="alice-obs__dropzone-sub">CSV / XLSX / XLS</div>
        </div>
      </div>
    </div>

    <!-- ============== Populated state ============== -->
    <template v-else>
      <!-- Stats trio -->
      <div class="alice-obs__stats">
        <div class="alice-obs__stat-tile">
          <div class="alice-obs__stat-row">
            <span
              class="alice-obs__stat-mark"
              style="background: var(--vp-black)"
            />
            <span class="alice-obs__stat-value">{{ notes.length }}</span>
          </div>
          <div class="alice-obs__stat-label">Total notes</div>
          <div class="alice-obs__stat-sub">{{ totalSubLabel }}</div>
        </div>
        <div class="alice-obs__stat-tile">
          <div class="alice-obs__stat-row">
            <span
              class="alice-obs__stat-mark"
              style="background: var(--vp-turquoise-100)"
            />
            <span class="alice-obs__stat-value">{{ counts.observation }}</span>
          </div>
          <div class="alice-obs__stat-label">Observations</div>
          <div class="alice-obs__stat-sub">behavior &amp; reactions</div>
        </div>
        <div class="alice-obs__stat-tile">
          <div class="alice-obs__stat-row">
            <span
              class="alice-obs__stat-mark"
              style="background: var(--vp-geel-100)"
            />
            <span class="alice-obs__stat-value">{{ counts.quote }}</span>
          </div>
          <div class="alice-obs__stat-label">Quotes</div>
          <div class="alice-obs__stat-sub">verbatim from participants</div>
        </div>
      </div>

      <!-- Composer (slide-down) -->
      <transition name="alice-composer">
        <section v-if="composerOpen" class="alice-obs__composer">
          <header class="alice-obs__composer-head">
            <span class="alice-obs__composer-mark" />
            <h2 class="alice-obs__composer-title">New note</h2>
            <button
              class="alice-obs__icon-btn"
              type="button"
              title="Close"
              @click="closeComposer"
            >
              ×
            </button>
          </header>

          <div class="alice-obs__composer-grid">
            <label class="alice-obs__field">
              <span class="alice-obs__label">Participant</span>
              <input
                v-model="note.participant"
                class="alice-obs__input"
                type="text"
                placeholder="P-1042 · Sam"
                :list="suggestionsId('participant')"
              />
              <datalist :id="suggestionsId('participant')">
                <option
                  v-for="(s, si) in participantSuggestions"
                  :key="si"
                  :value="s"
                />
              </datalist>
            </label>

            <div class="alice-obs__field">
              <span class="alice-obs__label">Type</span>
              <div class="alice-obs__chip-row">
                <button
                  v-for="t in ['observation', 'quote']"
                  :key="t"
                  type="button"
                  class="alice-obs__chip-btn"
                  :class="{ 'alice-obs__chip-btn--on': note.type === t }"
                  @click="note.type = t"
                >
                  {{ t === "observation" ? "Observation" : "Quote" }}
                </button>
              </div>
            </div>

            <div class="alice-obs__field">
              <span class="alice-obs__label">Importance</span>
              <div class="alice-obs__chip-row">
                <button
                  v-for="imp in ['low', 'medium', 'high']"
                  :key="imp"
                  type="button"
                  class="alice-obs__chip-btn"
                  :class="{
                    'alice-obs__chip-btn--on': note.importance === imp,
                  }"
                  @click="note.importance = imp"
                >
                  {{ imp }}
                </button>
              </div>
            </div>

            <label class="alice-obs__field">
              <span class="alice-obs__label">Task</span>
              <input
                v-model="note.task"
                class="alice-obs__input"
                type="text"
                placeholder="Welke taak voerde de deelnemer uit?"
                :list="suggestionsId('task')"
              />
              <datalist :id="suggestionsId('task')">
                <option
                  v-for="(s, si) in taskSuggestions"
                  :key="si"
                  :value="s"
                />
              </datalist>
            </label>

            <label class="alice-obs__field">
              <span class="alice-obs__label">Context</span>
              <input
                v-model="note.context"
                class="alice-obs__input"
                type="text"
                placeholder="Where / when"
                :list="suggestionsId('context')"
              />
              <datalist :id="suggestionsId('context')">
                <option
                  v-for="(s, si) in contextSuggestions"
                  :key="si"
                  :value="s"
                />
              </datalist>
            </label>

            <label class="alice-obs__field">
              <span class="alice-obs__label">Label</span>
              <input
                v-model="note.label"
                class="alice-obs__input"
                type="text"
                placeholder="Korte tag"
                :list="suggestionsId('label')"
              />
              <datalist :id="suggestionsId('label')">
                <option
                  v-for="(s, si) in labelSuggestions"
                  :key="si"
                  :value="s"
                />
              </datalist>
            </label>

            <label class="alice-obs__field alice-obs__field--span">
              <span class="alice-obs__label">Input</span>
              <textarea
                v-model="note.input"
                class="alice-obs__textarea"
                rows="4"
                placeholder="Wat heb je gezien of gehoord?"
              />
            </label>

            <label class="alice-obs__field alice-obs__field--span">
              <span class="alice-obs__label">Researcher note</span>
              <input
                v-model="note.note"
                class="alice-obs__input"
                type="text"
                placeholder="Optionele extra duiding"
              />
            </label>
          </div>

          <footer class="alice-obs__composer-foot">
            <span class="alice-obs__composer-hint"
              >⌘ + Enter to save and add another</span
            >
            <div class="alice-obs__composer-actions">
              <button
                class="alice-obs__head-btn"
                type="button"
                @click="closeComposer"
              >
                <span>Cancel</span>
              </button>
              <button
                class="alice-obs__head-btn alice-obs__head-btn--primary"
                type="button"
                :disabled="!canSave || saving"
                @click="saveNote"
              >
                <Icon name="plus" :size="14" />
                <span>{{ saving ? "Saving…" : "Save &amp; next" }}</span>
              </button>
            </div>
          </footer>
        </section>
      </transition>

      <!-- Toolbar -->
      <div class="alice-obs__toolbar">
        <div class="alice-obs__search">
          <Icon name="search" :size="18" />
          <input
            v-model="search"
            class="alice-obs__search-input"
            type="text"
            placeholder="Search notes, participants, tasks…"
          />
          <span class="alice-obs__search-count"
            >{{ filtered.length }} of {{ notes.length }}</span
          >
        </div>
        <button class="alice-obs__sort" @click="cycleSort">
          <Icon name="sort" :size="18" />
          <span>{{ sortLabel }}</span>
          <Icon name="chevron-down" :size="14" />
        </button>
      </div>

      <!-- Filter chips -->
      <div class="alice-obs__filters">
        <button
          v-for="opt in typeFilters"
          :key="opt.key"
          type="button"
          class="alice-obs__chip-tab"
          :class="{ 'alice-obs__chip-tab--active': typeFilter === opt.key }"
          @click="typeFilter = opt.key"
        >
          <span>{{ opt.label }}</span>
          <span class="alice-obs__chip-tab-count">{{ opt.count }}</span>
        </button>
        <span class="alice-obs__filter-sep" />
        <button
          v-for="opt in importanceFilters"
          :key="opt.key"
          type="button"
          class="alice-obs__chip-tab"
          :class="{
            'alice-obs__chip-tab--active': importanceFilter === opt.key,
          }"
          @click="importanceFilter = opt.key"
        >
          <span>{{ opt.label }}</span>
          <span class="alice-obs__chip-tab-count">{{ opt.count }}</span>
        </button>
      </div>

      <!-- Table -->
      <div class="alice-obs__table">
        <div class="alice-obs__table-head">
          <div>#</div>
          <div>Note</div>
          <div>Participant</div>
          <div>Task</div>
          <div>Type</div>
          <div>Importance</div>
          <div>Time</div>
          <div></div>
        </div>

        <div v-if="!filtered.length" class="alice-obs__empty-row">
          <p v-if="notes.length">No notes match the current filters.</p>
          <p v-else>
            No notes yet. Click “Add note” to capture your first observation.
          </p>
        </div>

        <div
          v-for="(n, idx) in filtered"
          :key="n.uid || idx"
          class="alice-obs__row"
          :class="{ 'is-stagger-in': staggerIn }"
          :style="
            staggerIn ? { animationDelay: Math.min(idx, 7) * 40 + 'ms' } : null
          "
        >
          <div class="alice-obs__cell-num">
            {{ String(idx + 1).padStart(2, "0") }}
          </div>

          <div class="alice-obs__cell-note">
            <p class="alice-obs__note-input">
              {{ n.input || n.note || "—" }}
            </p>
            <div v-if="noteMeta(n)" class="alice-obs__note-meta">
              {{ noteMeta(n) }}
            </div>
          </div>

          <div class="alice-obs__cell-participant">
            {{ n.participant || "—" }}
          </div>

          <div class="alice-obs__cell-task">{{ n.task || "—" }}</div>

          <div class="alice-obs__cell-type">
            <span
              v-if="n.type"
              class="alice-obs__tag"
              :class="`alice-obs__tag--${n.type}`"
            >
              <span class="alice-obs__tag-dot" />
              {{ n.type }}
            </span>
            <span v-else>—</span>
          </div>

          <div class="alice-obs__cell-imp">
            <span
              v-if="n.importance"
              class="alice-obs__tag"
              :class="`alice-obs__tag--imp-${n.importance}`"
            >
              <span class="alice-obs__tag-dot" />
              {{ n.importance }}
            </span>
            <span v-else>—</span>
          </div>

          <div class="alice-obs__cell-time">
            <div>{{ formatTime(n.timestamp) }}</div>
            <div v-if="formatRel(n.timestamp)" class="alice-obs__time-sub">
              {{ formatRel(n.timestamp) }}
            </div>
          </div>

          <div class="alice-obs__cell-actions">
            <button
              v-if="!isGuest"
              class="alice-obs__icon-btn alice-obs__icon-btn--danger"
              title="Delete note"
              type="button"
              @click="deleteNote(n)"
            >
              <Icon name="trash" :size="14" />
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { db, storage, functions } from "@/firebase";
import {
  doc,
  collection,
  deleteDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";

import Icon from "@/components/alice/Icon.vue";
import VP1Pattern from "@/components/alice/VP1Pattern.vue";

const SORTS = [
  { key: "recent", label: "Most recent" },
  { key: "oldest", label: "Oldest first" },
  { key: "importance", label: "By importance" },
];

const IMP_RANK = { high: 3, medium: 2, low: 1 };

function emptyNote() {
  return {
    participant: null,
    type: "observation",
    importance: null,
    task: null,
    context: null,
    label: null,
    input: null,
    note: null,
  };
}

export default {
  name: "ProjectObslog",
  components: { Icon, VP1Pattern },
  data() {
    return {
      notes: [],
      unsubscribe: null,
      search: "",
      sortIndex: 0,
      typeFilter: "all",
      importanceFilter: "all",
      composerOpen: false,
      saving: false,
      importing: false,
      replacing: false,
      dragOver: false,
      note: emptyNote(),
      staggerIn: true,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    obslog() {
      const list = this.project?.obslogs;
      return Array.isArray(list) && list.length ? list[0] : null;
    },
    hasObslog() {
      return !!this.obslog?.uid;
    },
    uploadButtonLabel() {
      if (this.replacing) return "Replacing…";
      if (this.importing) return "Uploading…";
      return this.hasObslog ? "Replace obslog" : "Upload obslog";
    },
    sortLabel() {
      return SORTS[this.sortIndex].label;
    },
    sortKey() {
      return SORTS[this.sortIndex].key;
    },
    counts() {
      const out = { observation: 0, quote: 0 };
      this.notes.forEach((n) => {
        if (n.type === "observation") out.observation += 1;
        else if (n.type === "quote") out.quote += 1;
      });
      return out;
    },
    impCounts() {
      const out = { low: 0, medium: 0, high: 0 };
      this.notes.forEach((n) => {
        if (n.importance && out[n.importance] !== undefined) {
          out[n.importance] += 1;
        }
      });
      return out;
    },
    typeFilters() {
      return [
        { key: "all", label: "All", count: this.notes.length },
        {
          key: "observation",
          label: "Observations",
          count: this.counts.observation,
        },
        { key: "quote", label: "Quotes", count: this.counts.quote },
      ];
    },
    importanceFilters() {
      return [
        { key: "all", label: "Any", count: this.notes.length },
        { key: "high", label: "High", count: this.impCounts.high },
        { key: "medium", label: "Medium", count: this.impCounts.medium },
        { key: "low", label: "Low", count: this.impCounts.low },
      ];
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let list = this.notes;
      if (this.typeFilter !== "all") {
        list = list.filter((n) => n.type === this.typeFilter);
      }
      if (this.importanceFilter !== "all") {
        list = list.filter((n) => n.importance === this.importanceFilter);
      }
      if (q) {
        list = list.filter((n) => {
          return [n.input, n.note, n.participant, n.task, n.context, n.label]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
        });
      }
      const copy = [...list];
      if (this.sortKey === "oldest") {
        copy.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      } else if (this.sortKey === "importance") {
        copy.sort(
          (a, b) =>
            (IMP_RANK[b.importance] || 0) - (IMP_RANK[a.importance] || 0) ||
            (b.timestamp || 0) - (a.timestamp || 0)
        );
      } else {
        copy.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      }
      return copy;
    },
    totalSubLabel() {
      if (!this.notes.length) return "—";
      const high = this.impCounts.high;
      if (high > 0) return `${high} marked high`;
      return "no high-importance yet";
    },
    canSave() {
      return !!String(this.note.input || "").trim();
    },
    participantSuggestions() {
      return this.uniqueValues("participant");
    },
    taskSuggestions() {
      return this.uniqueValues("task");
    },
    contextSuggestions() {
      return this.uniqueValues("context");
    },
    labelSuggestions() {
      return this.uniqueValues("label");
    },
  },
  watch: {
    obslog: {
      immediate: true,
      handler(curr, prev) {
        const newId = curr?.uid;
        const oldId = prev?.uid;
        if (newId === oldId) return;
        this.subscribeToNotes(newId);
      },
    },
  },
  mounted() {
    // Let the list cascade in once, then stop gating so re-filtering/sorting
    // doesn't replay the entrance.
    this._staggerTimer = setTimeout(() => {
      this.staggerIn = false;
    }, 700);
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
    clearTimeout(this._staggerTimer);
  },
  methods: {
    suggestionsId(field) {
      return `alice-obs-suggestions-${field}-${this.project?.uid || "x"}`;
    },
    uniqueValues(field) {
      const set = new Set();
      this.notes.forEach((n) => {
        if (n[field]) set.add(n[field]);
      });
      return [...set].slice(0, 12);
    },
    subscribeToNotes(obslogUid) {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.notes = [];
      const projectId = this.project?.uid || this.$route.params.id;
      if (!obslogUid || !projectId) return;
      this.unsubscribe = onSnapshot(
        collection(db, "projects", projectId, "obslog", obslogUid, "input"),
        (snapshot) => {
          this.notes = snapshot.docs.map((d) => ({
            ...d.data(),
            uid: d.data().uid || d.id,
            id: d.id,
          }));
        }
      );
    },

    cycleSort() {
      this.sortIndex = (this.sortIndex + 1) % SORTS.length;
    },

    toggleComposer() {
      if (this.composerOpen) this.closeComposer();
      else this.openComposer();
    },
    openComposer() {
      this.composerOpen = true;
    },
    closeComposer() {
      this.composerOpen = false;
      this.note = emptyNote();
    },

    noteMeta(n) {
      const parts = [];
      if (n.context) parts.push(n.context);
      if (n.label) parts.push(n.label);
      if (n.note && n.note !== n.input) parts.push(n.note);
      return parts.join(" · ");
    },

    formatTime(ts) {
      if (!ts) return "—";
      const d = new Date(ts * 1000);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatRel(ts) {
      if (!ts) return "";
      const d = new Date(ts * 1000);
      if (Number.isNaN(d.getTime())) return "";
      const diff = Math.round((Date.now() - d.getTime()) / 1000);
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
    },

    async saveNote() {
      if (!this.canSave) return;
      if (!this.obslog?.uid) return;
      this.saving = true;
      try {
        const projectId = this.project.uid;
        const obslogId = this.obslog.uid;
        const payload = {
          ...this.note,
          timestamp: Math.floor(Date.now() / 1000),
          // Stamp the author so Firestore rules can scope guest edits to their
          // own rows (guests may add entries, not rewrite colleagues').
          createdBy: this.$store.state.user?.uid || null,
        };
        const docRef = await addDoc(
          collection(db, "projects", projectId, "obslog", obslogId, "input"),
          payload
        );
        await updateDoc(
          doc(
            db,
            "projects",
            projectId,
            "obslog",
            obslogId,
            "input",
            docRef.id
          ),
          { uid: docRef.id }
        );
        // Reset for next entry; preserve participant/task/context/label
        // because they typically apply to a run of consecutive notes.
        const persisted = {
          participant: this.note.participant,
          task: this.note.task,
          context: this.note.context,
          label: this.note.label,
        };
        this.note = { ...emptyNote(), ...persisted };
      } catch (err) {
        console.error("saveNote failed:", err);
      } finally {
        this.saving = false;
      }
    },

    async deleteNote(n) {
      if (!this.obslog?.uid) return;
      const target = n.uid || n.id;
      if (!target) return;
      if (!confirm(`Delete this note?`)) return;
      try {
        await deleteDoc(
          doc(
            db,
            "projects",
            this.project.uid,
            "obslog",
            this.obslog.uid,
            "input",
            target
          )
        );
      } catch (err) {
        console.error("deleteNote failed:", err);
      }
    },

    /* ============== Spreadsheet upload ============== */
    onUploadButtonClick() {
      if (this.hasObslog) this.confirmReplaceObslog();
      else this.triggerObslogUpload();
    },
    confirmReplaceObslog() {
      const ok = window.confirm(
        "Replace the current obslog? This deletes all existing notes and cannot be undone."
      );
      if (!ok) return;
      this.triggerObslogUpload();
    },
    triggerObslogUpload() {
      if (this.$refs.uploadRef) this.$refs.uploadRef.click();
    },
    resetUploadValue(event) {
      if (event?.target) event.target.value = "";
    },
    onFileChange() {
      const files = Array.from(this.$refs.uploadRef?.files || []);
      if (!files.length) return;
      this.handleSelectedFile(files[0]);
    },
    onDrop(event) {
      this.dragOver = false;
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length) this.handleSelectedFile(files[0]);
    },
    async handleSelectedFile(file) {
      if (!file) return;
      if (this.hasObslog) {
        this.replacing = true;
        this.$store.commit("setLoading", true);
        try {
          await this.deleteExistingObslog();
        } catch (err) {
          console.error("Replace obslog failed during delete:", err);
          this.replacing = false;
          this.$store.commit("setLoading", false);
          return;
        }
        this.replacing = false;
      }
      this.uploadObslog(file);
    },
    async deleteExistingObslog() {
      const projectId = this.project?.uid;
      const existing = this.obslog;
      if (!projectId || !existing?.uid) return;
      const obslogId = existing.uid;

      const inputSnap = await getDocs(
        collection(db, "projects", projectId, "obslog", obslogId, "input")
      );
      const docs = inputSnap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }

      if (existing.storage_path) {
        try {
          await deleteObject(ref(storage, existing.storage_path));
        } catch (err) {
          console.warn("Obslog storage delete skipped:", err);
        }
      }

      await deleteDoc(doc(db, "projects", projectId, "obslog", obslogId));
      await this.$store.dispatch("fetchProject", projectId);
    },
    uploadObslog(file) {
      if (!file) return;
      this.importing = true;
      this.$store.commit("setLoading", true);
      const projectId = this.project.uid;
      const fileExtension = file.name.split(".").pop();

      (async () => {
        try {
          const docRef = await addDoc(
            collection(db, "projects", projectId, "obslog"),
            {
              title: file.name.split(".")[0],
              created: Math.round(Date.now() / 1000),
            }
          );
          await updateDoc(doc(db, "projects", projectId, "obslog", docRef.id), {
            uid: docRef.id,
          });

          const storagePath = `projects/${projectId}/obslog/${docRef.id}.${fileExtension}`;
          const path = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(path, file);

          uploadTask.on(
            "state_changed",
            () => {
              /* progress hook reserved */
            },
            (error) => {
              console.error("Obslog upload error", error);
              this.importing = false;
              this.$store.commit("setLoading", false);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              await updateDoc(
                doc(db, "projects", projectId, "obslog", docRef.id),
                { storage_link: downloadURL, storage_path: storagePath }
              );
              try {
                const processObslog = httpsCallable(functions, "processObslog");
                await processObslog({
                  project_uid: projectId,
                  storage_link: downloadURL,
                  obslog_uid: docRef.id,
                });
              } catch (err) {
                console.error("processObslog failed:", err);
              }
              await this.$store.dispatch("fetchProject", projectId);
              this.importing = false;
              this.$store.commit("setLoading", false);
            }
          );
        } catch (err) {
          console.error("Obslog upload failed:", err);
          this.importing = false;
          this.$store.commit("setLoading", false);
        }
      })();
    },
  },
};
</script>

<style scoped lang="scss">
.alice-obs {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-obs__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-obs__head-main {
  flex: 1;
  min-width: 0;
}
.alice-obs__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-obs__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-obs__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-obs__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-obs__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.alice-obs__head-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-obs__head-btn:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-obs__head-btn--primary {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-obs__head-btn--primary:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
  border-color: var(--accent-100);
}
.alice-obs__head-btn--active {
  background: var(--accent-100);
  color: var(--vp-black);
  border-color: var(--accent-100);
}
.alice-obs__head-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Empty ============== */
.alice-obs__empty {
  padding: 32px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-obs__empty--upload {
  position: relative;
  padding: 64px 32px;
  text-align: center;
  overflow: hidden;
}
.alice-obs__empty-pattern {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.alice-obs__empty-inner {
  position: relative;
  max-width: 520px;
  margin: 0 auto;
}
.alice-obs__empty-headline {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  color: var(--vp-black);
  letter-spacing: -0.01em;
}
.alice-obs__empty-body {
  margin: 0 0 24px;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
}

/* ============== Stats ============== */
.alice-obs__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-obs__stat-tile {
  position: relative;
  padding: 20px 32px;
}
.alice-obs__stat-tile + .alice-obs__stat-tile {
  border-left: 1px solid var(--border-hairline);
}
.alice-obs__stat-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.alice-obs__stat-mark {
  width: 10px;
  height: 10px;
  align-self: center;
  flex-shrink: 0;
}
.alice-obs__stat-value {
  font-family: var(--font-display);
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-obs__stat-label {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-obs__stat-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Composer ============== */
.alice-obs__composer {
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
}
.alice-obs__composer-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-obs__composer-mark {
  width: 12px;
  height: 12px;
  background: var(--accent-100);
  flex-shrink: 0;
}
.alice-obs__composer-title {
  margin: 0;
  flex: 1;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.alice-obs__composer-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;
  padding: 20px 32px;
}
.alice-obs__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.alice-obs__field--span {
  grid-column: 1 / -1;
}
.alice-obs__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-obs__input,
.alice-obs__textarea {
  width: 100%;
  padding: 10px 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  outline: none;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.4;
  color: var(--vp-black);
  border-radius: 0;
  resize: vertical;
}
.alice-obs__input:focus,
.alice-obs__textarea:focus {
  border-color: var(--vp-black);
}
.alice-obs__input::placeholder,
.alice-obs__textarea::placeholder {
  color: var(--fg-3);
}

.alice-obs__chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.alice-obs__chip-btn {
  padding: 7px 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.alice-obs__chip-btn:hover {
  border-color: var(--vp-black);
  color: var(--vp-black);
}
.alice-obs__chip-btn--on,
.alice-obs__chip-btn--on:hover {
  background: var(--vp-black);
  color: var(--vp-white);
  border-color: var(--vp-black);
}

.alice-obs__composer-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 32px 20px;
}
.alice-obs__composer-hint {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-obs__composer-actions {
  display: flex;
  gap: 8px;
}

.alice-composer-enter-active {
  transition: transform var(--dur-base) var(--ease-out),
    opacity var(--dur-base) var(--ease-out),
    max-height var(--dur-base) var(--ease-out);
}
.alice-composer-leave-active {
  transition: transform var(--dur-exit) var(--ease-out),
    opacity var(--dur-exit) var(--ease-out),
    max-height var(--dur-exit) var(--ease-out);
}
.alice-composer-enter-from,
.alice-composer-leave-to {
  transform: translateY(-8px);
  opacity: 0;
  max-height: 0;
}

/* Staggered list entrance (decorative, plays once on mount) */
@keyframes alice-obs-fade-in-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.alice-obs__row.is-stagger-in {
  animation: alice-obs-fade-in-up var(--dur-base) var(--ease-out) both;
}
.alice-composer-enter-to,
.alice-composer-leave-from {
  max-height: 800px;
}

/* ============== Toolbar ============== */
.alice-obs__toolbar {
  display: flex;
  align-items: stretch;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-obs__search {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  border-right: 1px solid var(--border-hairline);
  color: var(--vp-black);
}
.alice-obs__search-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--vp-black);
}
.alice-obs__search-input::placeholder {
  color: var(--fg-3);
}
.alice-obs__search-count {
  flex-shrink: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-obs__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
}
.alice-obs__sort:hover {
  background: var(--bg-muted);
}

/* ============== Filter chips ============== */
.alice-obs__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-obs__filter-sep {
  width: 1px;
  height: 24px;
  background: var(--border-hairline);
  margin: 0 4px;
}
.alice-obs__chip-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-2);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.alice-obs__chip-tab:hover {
  border-color: var(--vp-black);
  color: var(--vp-black);
}
.alice-obs__chip-tab--active,
.alice-obs__chip-tab--active:hover {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
  font-weight: 700;
}
.alice-obs__chip-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--fg-3);
  background: var(--bg-muted);
}
.alice-obs__chip-tab--active .alice-obs__chip-tab-count {
  background: var(--vp-grijs-160);
  color: var(--vp-white);
}

/* ============== Table ============== */
.alice-obs__table {
  display: flex;
  flex-direction: column;
}
.alice-obs__table-head,
.alice-obs__row {
  display: grid;
  grid-template-columns: 56px minmax(0, 2fr) 140px 140px 110px 110px 110px 60px;
  gap: 16px;
  align-items: center;
  padding: 0 32px;
}
.alice-obs__table-head {
  height: 44px;
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  position: sticky;
  top: 0;
  z-index: 1;
}
.alice-obs__row {
  position: relative;
  padding-top: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-hairline);
  transition: background 0.12s ease;
}
.alice-obs__row:hover {
  background: var(--bg-muted);
}
.alice-obs__row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
}
.alice-obs__row:hover::before {
  background: var(--accent-100);
}

.alice-obs__cell-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  background: var(--vp-grijs-20);
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-3);
}

.alice-obs__cell-note {
  min-width: 0;
}
.alice-obs__note-input {
  margin: 0;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--vp-black);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.alice-obs__note-meta {
  margin-top: 4px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--fg-3);
}

.alice-obs__cell-participant,
.alice-obs__cell-task {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alice-obs__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  line-height: 1;
}
.alice-obs__tag-dot {
  width: 6px;
  height: 6px;
  background: currentColor;
}
.alice-obs__tag--observation {
  color: var(--vp-turquoise-160);
  background: var(--vp-turquoise-20);
}
.alice-obs__tag--quote {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.alice-obs__tag--imp-low {
  color: var(--vp-grijs-160);
  background: var(--vp-grijs-20);
}
.alice-obs__tag--imp-medium {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.alice-obs__tag--imp-high {
  color: var(--vp-rood-160);
  background: var(--vp-rood-20);
}

.alice-obs__cell-time {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--vp-black);
  letter-spacing: 0.04em;
}
.alice-obs__time-sub {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  letter-spacing: 0;
  margin-top: 2px;
}

.alice-obs__cell-actions {
  display: flex;
  justify-content: flex-end;
}
.alice-obs__icon-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  font-family: var(--font-display);
  font-size: 18px;
  line-height: 1;
  transition: border-color 0.12s ease, color 0.12s ease, background 0.12s ease;
}
.alice-obs__icon-btn:hover {
  border-color: var(--vp-black);
}
.alice-obs__icon-btn--danger {
  color: var(--vp-rood-100);
}
.alice-obs__icon-btn--danger:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}

.alice-obs__empty-row {
  padding: 32px;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
}

/* ============== Drop zone ============== */
.alice-obs__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  text-align: center;
  font-family: var(--font-ui);
  color: var(--fg-3);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.alice-obs__dropzone:hover,
.alice-obs__dropzone.is-dragover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-obs__dropzone-text {
  font-size: 14px;
  font-weight: 600;
}
.alice-obs__dropzone-link {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-obs__dropzone-sub {
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Responsive ============== */
@media (max-width: 1279px) {
  .alice-obs__head {
    padding: 24px 20px 20px;
    flex-direction: column;
  }
  .alice-obs__head-actions {
    align-self: flex-start;
  }
  .alice-obs__stats {
    grid-template-columns: 1fr;
  }
  .alice-obs__stat-tile + .alice-obs__stat-tile {
    border-left: none;
    border-top: 1px solid var(--border-hairline);
  }
  .alice-obs__composer-grid {
    grid-template-columns: 1fr;
    padding: 16px 20px;
  }
  .alice-obs__composer-foot {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 20px 20px;
  }
  .alice-obs__table-head,
  .alice-obs__row {
    grid-template-columns: 40px minmax(0, 2fr) 110px 110px 60px;
    padding: 0 20px;
  }
  .alice-obs__table-head > div:nth-child(3),
  .alice-obs__table-head > div:nth-child(4),
  .alice-obs__table-head > div:nth-child(7),
  .alice-obs__row .alice-obs__cell-participant,
  .alice-obs__row .alice-obs__cell-task,
  .alice-obs__row .alice-obs__cell-time {
    display: none;
  }
}
</style>
